import { useState, useEffect, useCallback, useRef } from "react";
import { validateUsernameFormat } from "../../utils/usernameValidation";
import { useCheckUsernameAvailability } from "./useCheckUsernameAvailability";

/**
 * Custom hook for username validation with debounced availability checking
 *
 * Provides real-time format validation and debounced server-side availability checking.
 * Requirements: 3.3, 3.4, 3.5, 3.6, 3.7, 3.8
 *
 * @returns {Object} Validation state and functions
 * @property {Function} validateUsername - Function to validate a username
 * @property {boolean} isValidating - True when checking availability
 * @property {boolean|null} isAvailable - Username availability status (null if not checked yet)
 * @property {string|null} validationError - Current validation error message
 * @property {boolean} isValid - True if username passes all validations
 * @property {Function} reset - Function to reset validation state
 */
export const useUsernameValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isValid, setIsValid] = useState(false);

  const debounceTimerRef = useRef(null);
  const lastCheckedUsernameRef = useRef("");

  const { mutate: checkAvailability, isLoading: isCheckingAvailability } =
    useCheckUsernameAvailability();

  /**
   * Validates username format and triggers availability check if format is valid
   * @param {string} username - The username to validate
   */
  const validateUsername = useCallback(
    (username) => {
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // Store current username in ref for comparison in async callbacks
      const usernameToCheck = username;

      // Reset availability state when username changes
      setIsAvailable(null);
      setIsValid(false);

      // Handle empty username
      if (!usernameToCheck || usernameToCheck.trim() === "") {
        setValidationError(null);
        setIsValidating(false);
        return;
      }

      // Perform format validation (immediate, client-side)
      const formatValidation = validateUsernameFormat(usernameToCheck);

      if (!formatValidation.isValid) {
        // Format validation failed - show specific error message
        setValidationError(formatValidation.error);
        setIsValidating(false);
        setIsAvailable(null);
        setIsValid(false);
        return;
      }

      // Format is valid, clear format errors
      setValidationError(null);

      // Check if we've already validated this username
      if (
        usernameToCheck === lastCheckedUsernameRef.current &&
        isAvailable !== null
      ) {
        // Use cached result
        setIsValid(isAvailable);
        if (!isAvailable) {
          setValidationError("Username already taken");
        }
        return;
      }

      // Debounce the availability check (500ms)
      setIsValidating(true);
      debounceTimerRef.current = setTimeout(() => {
        // Perform availability check (server-side)
        checkAvailability(usernameToCheck, {
          onSuccess: (data) => {
            // Store the checked username
            lastCheckedUsernameRef.current = usernameToCheck;
            setIsAvailable(data.available);
            setIsValidating(false);

            if (data.available) {
              setIsValid(true);
              setValidationError(null);
            } else {
              setIsValid(false);
              setValidationError("Username already taken");
            }
          },
          onError: (error) => {
            setIsValidating(false);
            setIsAvailable(null);
            setIsValid(false);

            // Provide specific error messages based on error type
            if (error.code === "ECONNABORTED") {
              setValidationError("Request timed out. Please try again.");
            } else if (error.response?.status === 400) {
              // Server-side validation error
              setValidationError(
                error.response?.data?.errors?.[0]?.msg ||
                  "Invalid username format"
              );
            } else if (
              error.response?.status === 401 ||
              error.response?.status === 403
            ) {
              setValidationError(
                "Authentication error. Please refresh the page."
              );
            } else if (error.response?.status >= 500) {
              setValidationError("Server error. Please try again in a moment.");
            } else if (error.request) {
              // Network error - no response received
              setValidationError(
                "Network error. Please check your connection."
              );
            } else {
              setValidationError(
                "Unable to check availability. Please try again."
              );
            }
          },
        });
      }, 500);
    },
    [checkAvailability, isAvailable]
  );

  /**
   * Reset validation state
   */
  const reset = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setIsValidating(false);
    setIsAvailable(null);
    setValidationError(null);
    setIsValid(false);
    lastCheckedUsernameRef.current = "";
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Update isValidating state when checking availability
  useEffect(() => {
    if (isCheckingAvailability) {
      setIsValidating(true);
    }
  }, [isCheckingAvailability]);

  return {
    validateUsername,
    isValidating,
    isAvailable,
    validationError,
    isValid,
    reset,
  };
};
